// Normalized application error for every non-OK API response. Nothing
// outside src/api/ imports the RFC 9457 wire shape; this class is the
// boundary type the rest of the app sees.

export interface FieldErrorSpec {
    readonly path: string;
    readonly message: string;
    readonly code: string;
}

/** The client's stand-in when the body carried no code. */
export const NO_CODE = "UNKNOWN";

export const CONSTRAINT_VIOLATION = "Validation.ConstraintViolation";
export const ALREADY_EXISTS = "Conflict.AlreadyExists";
export const PRECONDITION_FAILED = "Precondition.Failed";

export class ApiError extends Error {
    readonly status: number;
    /** The problem's error code (`General.NotFound`), else `NO_CODE`. */
    readonly code: string;
    /** The status's reason phrase; never shown. Present only on a problem body. */
    readonly title?: string;
    /** The server's message for this occurrence. Never sent on a 5xx. */
    readonly detail?: string;
    readonly instance?: string;
    readonly requestId?: string;
    readonly errors?: ReadonlyArray<FieldErrorSpec>;
    /** Values `code`'s copy reads, e.g. `{ templateId, surveyCount }`. */
    readonly params?: Readonly<Record<string, string | null>>;

    constructor(init: {
        status: number;
        code: string;
        title?: string;
        detail?: string;
        instance?: string;
        requestId?: string;
        errors?: ReadonlyArray<FieldErrorSpec>;
        params?: Record<string, string | null>;
    }) {
        super(`API error ${init.status}: ${init.code}`);
        this.name = "ApiError";
        this.status = init.status;
        this.code = init.code;
        this.title = init.title;
        this.detail = init.detail;
        this.instance = init.instance;
        this.requestId = init.requestId;
        this.errors = init.errors;
        this.params = init.params;
    }

    get isValidation(): boolean {
        return Array.isArray(this.errors) && this.errors.length > 0;
    }

    /** Not the server's problem body: a proxy's or an HTML error page. */
    get opaque(): boolean {
        return this.code === NO_CODE && this.title === undefined;
    }

    /** A count param, e.g. `countParam("responseCount")`; undefined unless a whole number. */
    countParam(name: string): number | undefined {
        const raw = this.params?.[name];
        if (raw === undefined || raw === null || raw === "") return undefined;
        const count = Number(raw);
        return Number.isInteger(count) && count >= 0 ? count : undefined;
    }
}

/** 403: the session is valid but the account isn't authorized here. */
export function isForbidden(error: unknown): error is ApiError {
    return error instanceof ApiError && error.status === 403;
}

/** The server refused because of other data, e.g. a delete still referenced. */
export function isConstraintViolation(error: unknown): error is ApiError {
    return error instanceof ApiError && error.code === CONSTRAINT_VIOLATION;
}
